import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import * as cognitoAuth from './cognitoAuth';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [status, setStatus] = useState('loading'); // loading | signed-out | signed-in
  const [email, setEmail] = useState(null);

  useEffect(() => {
    cognitoAuth
      .getCurrentSession()
      .then((current) => {
        if (current) {
          setEmail(current.session.getIdToken().payload.email);
          setStatus('signed-in');
        } else {
          setStatus('signed-out');
        }
      })
      .catch(() => setStatus('signed-out'));
  }, []);

  const signIn = useCallback(async (emailValue, password) => {
    const result = await cognitoAuth.signIn(emailValue, password);
    if (result.newPasswordRequired) {
      return result; // caller renders the "set new password" step
    }
    setEmail(result.session.getIdToken().payload.email);
    setStatus('signed-in');
    return result;
  }, []);

  const completeNewPassword = useCallback(async (user, newPassword, userAttributes) => {
    const result = await cognitoAuth.completeNewPassword(user, newPassword, userAttributes);
    setEmail(result.session.getIdToken().payload.email);
    setStatus('signed-in');
    return result;
  }, []);

  const signOut = useCallback(() => {
    cognitoAuth.signOut();
    setEmail(null);
    setStatus('signed-out');
  }, []);

  // No email/status change on success - changing your own password doesn't
  // affect the current session, just passed straight through to Cognito.
  const changePassword = useCallback((oldPassword, newPassword) => {
    return cognitoAuth.changePassword(oldPassword, newPassword);
  }, []);

  return (
    <AuthContext.Provider
      value={{ status, email, signIn, completeNewPassword, signOut, changePassword }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
